import React from 'react';
import ReactDOM from 'react-dom';
import { trackEvent } from '../../utils/analytics';
import './HelpModal.css';

interface HelpModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const HelpModal: React.FC<HelpModalProps> = ({ isOpen, onClose }) => {
  // Track help guide access when modal opens
  React.useEffect(() => {
    if (isOpen) {
      trackEvent('accessed_help_guide');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return ReactDOM.createPortal(
    <div className="help-modal-overlay" onClick={handleOverlayClick}>
      <div className="help-modal">
        <div className="help-modal-header">
          <div className="help-modal-title">
            <h2>ThreadIt Help Guide</h2>
            <p>Learn how to use ThreadIt's AI-powered conversational canvas</p>
          </div>
          <button className="help-modal-close" onClick={onClose}>
            ✕
          </button>
        </div>
        
        {/* Quick Navigation Pills */}
        <div className="help-nav-pills">
          <button className="nav-pill" onClick={() => document.getElementById('getting-started')?.scrollIntoView({ behavior: 'smooth' })}>
            🎯 Getting Started
          </button>
          <button className="nav-pill" onClick={() => document.getElementById('ai-conversations')?.scrollIntoView({ behavior: 'smooth' })}>
            💬 AI Conversations
          </button>
          <button className="nav-pill" onClick={() => document.getElementById('branching')?.scrollIntoView({ behavior: 'smooth' })}>
            🌿 Branching
          </button>
          <button className="nav-pill" onClick={() => document.getElementById('merging')?.scrollIntoView({ behavior: 'smooth' })}>
            🔄 Merging
          </button>
          <button className="nav-pill" onClick={() => document.getElementById('navigation')?.scrollIntoView({ behavior: 'smooth' })}>
            🎨 Navigation
          </button>
          <button className="nav-pill" onClick={() => document.getElementById('shortcuts')?.scrollIntoView({ behavior: 'smooth' })}>
            ⌨️ Shortcuts
          </button>
          <button className="nav-pill" onClick={() => document.getElementById('project-management')?.scrollIntoView({ behavior: 'smooth' })}>
            💾 Projects
          </button>
          <button className="nav-pill" onClick={() => document.getElementById('tips')?.scrollIntoView({ behavior: 'smooth' })}>
            🔧 Tips
          </button>
        </div>
        
        <div className="help-modal-content">
          <div className="help-section" id="getting-started">
            <h3>🎯 Getting Started</h3>
            <div className="help-item">
              <h4>Creating Your First Note</h4>
              <ul>
                <li><strong>Click the <div style={{display: 'inline-flex', alignItems: 'center', margin: '0 4px'}}><div style={{padding: '2px', background: 'linear-gradient(135deg, #f472b6, #fb923c)', borderRadius: '9999px', boxShadow: '0 4px 8px -2px rgba(0, 0, 0, 0.1)'}}><div style={{background: 'white', color: '#374151', borderRadius: '9999px', width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', pointerEvents: 'none'}}><svg style={{width: '12px', height: '12px'}} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg></div></div></div> button</strong> in the toolbar to create a new note</li>
                <li><strong>Double-click</strong> on any note to edit its content</li>
                <li><strong>Drag notes</strong> to reposition them on the canvas</li>
                <li><strong>Select notes</strong> by clicking on them (hold Ctrl/Cmd for multiple)</li>
                <li><strong>Get help anytime</strong> by clicking the ❓ button on the right side</li>
              </ul>
            </div>
          </div>

          <div className="help-section" id="ai-conversations">
            <h3>💬 AI Conversations</h3>
            <div className="help-item">
              <h4>Starting a Chat</h4>
              <ul>
                <li><strong>Click on any note</strong> to open the chat pane on the right</li>
                <li><strong>Type your message</strong> and press Enter to start chatting with AI</li>
                <li><strong>Choose a persona</strong> by clicking on the <span style={{display: 'inline-flex', alignItems: 'center', gap: '2px', padding: '2px 6px', background: 'rgba(25, 118, 210, 0.1)', borderRadius: '12px', border: '1px solid rgba(25, 118, 210, 0.3)', fontSize: '12px'}}>👤</span> button (e.g., "System Architect", "Creative Writer")</li>
                <li><strong>Upload files</strong> by dragging them into the chat or clicking the upload button</li>
              </ul>
            </div>
            
            <div className="help-item">
              <h4>Advanced Chat Features</h4>
              <ul>
                <li><strong>Web Search:</strong> Enable web search to get real-time information</li>
                <li><strong>File Attachments:</strong> Supports PDF files</li>
                <li><strong>Markdown Support:</strong> AI responses support rich formatting</li>
                <li><strong>Mermaid Diagrams:</strong> Chat can render mermaid diagrams</li>
                <li><strong>Clickable Headings:</strong> Click on headings in AI responses to create branches</li>
              </ul>
            </div>
          </div>

          <div className="help-section" id="branching">
            <h3>🌿 Branching Conversations</h3>
            <div className="help-item">
              <h4>Creating Branches</h4>
              <ul>
                <li><strong>From AI Messages:</strong> Click on headings or bold text in AI responses</li>
                <li><strong>From User Messages:</strong> Use the <svg style={{display: 'inline', width: '16px', height: '16px', verticalAlign: 'middle', margin: '0 2px'}} viewBox="0 0 24 24" fill="none"><circle cx="6" cy="18" r="2" fill="currentColor"/><circle cx="6" cy="6" r="2" fill="currentColor"/><circle cx="18" cy="18" r="2" fill="currentColor"/><path d="M6 8v8a4 4 0 004 4h4" stroke="currentColor" strokeWidth="2" fill="none"/><path d="M6 6c0-1.104.896-2 2-2h4a4 4 0 014 4v10" stroke="currentColor" strokeWidth="2" fill="none"/></svg> "Create Branch" button below messages</li>
                <li><strong>Choose Inheritance:</strong> Select how much context to pass to the new branch</li>
                <li><strong>Color Coding:</strong> Each branch gets a unique color for easy identification</li>
              </ul>
            </div>
            
            <div className="help-item">
              <h4>Branch Types</h4>
              <ul>
                <li><strong>Full Context:</strong> Inherits entire conversation history</li>
                <li><strong>Summary Context:</strong> AI-generated summary of parent conversation</li>
                <li><strong>Empty Context:</strong> Fresh start with hierarchical relationship</li>
              </ul>
            </div>
          </div>

          <div className="help-section" id="merging">
            <h3>🔄 Merging & Synthesis</h3>
            <div className="help-item">
              <h4>Merging Branches</h4>
              <ul>
                <li><strong>Hover over branch notes</strong> to see the "Propose Merge" button</li>
                <li><strong>AI Summary:</strong> Automatically generates a summary of the branch conversation</li>
                <li><strong>Edit Summary:</strong> You can modify the AI-generated summary before merging</li>
                <li><strong>Merge Execution:</strong> Merges the branch back to its parent with the summary</li>
                <li><strong>Copy Messages:</strong> Use the <svg style={{display: 'inline', width: '16px', height: '16px', verticalAlign: 'middle', margin: '0 2px'}} viewBox="0 0 24 24" fill="none"><path d="M8 4v12a2 2 0 002 2h8a2 2 0 002-2V7.242a2 2 0 00-.602-1.43L16.083 2.57A2 2 0 0014.685 2H10a2 2 0 00-2 2z" stroke="currentColor" strokeWidth="2" fill="none"/><path d="M16 18v2a2 2 0 01-2 2H6a2 2 0 01-2-2V9a2 2 0 012-2h2" stroke="currentColor" strokeWidth="2" fill="none"/></svg> "Copy" button to copy a message to another note</li>
              </ul>
            </div>
            
            <div className="help-item">
              <h4>Cross-Branch Synthesis</h4>
              <ul>
                <li><strong>Coming soon</strong></li>
              </ul>
            </div>
          </div>

          <div className="help-section" id="navigation">
            <h3>🎨 Canvas Navigation</h3>
            <div className="help-item">
              <h4>Moving Around</h4>
              <ul>
                <li><strong>Pan:</strong> Click and drag on empty canvas to move around</li>
                <li><strong>Zoom:</strong> Use mouse wheel or zoom buttons in toolbar</li>
                <li><strong>Reset View:</strong> Click the zoom percentage button to reset</li>
                <li><strong>Selection Mode:</strong> Use the selection tool to select multiple notes</li>
              </ul>
            </div>
          </div>

          <div className="help-section" id="shortcuts">
            <h3>⌨️ Keyboard Shortcuts</h3>
            <div className="help-item">
              <h4>Essential Shortcuts</h4>
              <ul>
                <li><strong>Ctrl/Cmd + N:</strong> Create new note</li>
                <li><strong>Delete:</strong> Delete selected note(s)</li>
                <li><strong>Escape:</strong> Deselect all notes</li>
                <li><strong>Ctrl/Cmd + Shift + C:</strong> Toggle chat pane</li>
                <li><strong>F1 or Ctrl/Cmd + Shift + H:</strong> Open help guide</li>
              </ul>
            </div>
          </div>

          <div className="help-section" id="project-management">
            <h3>💾 Project Management</h3>
            <div className="help-item">
              <h4>Saving & Loading</h4>
              <ul>
                <li><strong>Save Project:</strong> Click the save icon in the toolbar</li>
                <li><strong>Load Project:</strong> Click the folder icon to load a saved project</li>
                <li><strong>File Format:</strong> Projects are saved as JSON files</li>
                <li><strong>Auto-save:</strong> Your work is automatically saved to the cloud every 30 seconds when you have unsaved changes</li>
              </ul>
            </div>
          </div>

          <div className="help-section" id="tips">
            <h3>🔧 Tips & Tricks</h3>
            <div className="help-item">
              <h4>Best Practices</h4>
              <ul>
                <li><strong>Start Simple:</strong> Begin with a main topic and branch out as needed</li>
                <li><strong>Use Personas:</strong> Different personas can help with different types of thinking</li>
                <li><strong>Regular Merging:</strong> Merge branches when they've served their purpose</li>
                <li><strong>File Organization:</strong> Attach relevant files to keep context together</li>
                <li><strong>Web Search:</strong> Use web search for real-time information and fact-checking</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default HelpModal;
